import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardPageProps } from '../../../lib/dashboard-page';
import { AuthButton } from '../components/auth-button';
import { AuthLayout } from '../components/auth-layout';
import { AuthLogo } from '../components/auth-logo';
import { FormErrorAlert } from '../components/form-error-alert';
import { ReturnToLoginLink } from '../components/return-to-login-link';
import { TextInput } from '../components/text-input';
import type { AuthFormError } from '../types';

const PAGE = 'features:auth.pages.reset_password_page' satisfies I18nPrefix;

export function ResetPasswordPage({ handlers }: DashboardPageProps) {
  const { apiHandler, navigationHandler, notificationHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<AuthFormError | null>(null);

  const form = useAppForm({
    defaultValues: {
      email: navigationHandler.query().email ?? '',
      password: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      setError(null);
      setLoading(true);
      try {
        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });
        await apiHandler.fetch({
          method: 'POST',
          path: '/reset-password',
          payload: {
            email: value.email,
            password: value.password,
            password_confirmation: value.confirmPassword,
            token: navigationHandler.params().token,
          },
        });

        notificationHandler.notify(
          t(`${PAGE}.success_title`),
          t(`${PAGE}.success_message`),
          'success',
        );
        navigationHandler.navigate('/login', { excludeSiteId: true, excludeSiteEnv: true });
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 422) {
          setError(err.data as { errors: Record<string, string[]> });
        } else if (err instanceof ApiResponseError && err.status === 429) {
          setError({ message: t(`${PAGE}.rate_limited_error`) });
        } else {
          setError({ message: t('errors:generic') });
        }
        setLoading(false);
      }
    },
  });

  return (
    <AuthLayout>
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <AuthLogo />
        <div className="mt-8 rounded-lg border border-spark-border-subtle bg-spark-surface p-10 shadow-xl sm:p-14">
          <div>
            <h2 className="mb-4 font-bold text-spark-text-primary text-xl sm:text-2xl">
              {t(`${PAGE}.heading`)}
            </h2>
          </div>

          <div className="mt-6">
            <form
              className="space-y-6"
              onSubmit={(event) => {
                event.preventDefault();
                void form.handleSubmit();
              }}
            >
              {error && <FormErrorAlert error={error} />}

              <form.Field name="email">
                {(field) => (
                  <TextInput
                    name="email"
                    type="email"
                    label={t(`${PAGE}.email_label`)}
                    autoComplete="email"
                    required
                    placeholder={t(`${PAGE}.email_placeholder`)}
                    value={field.state.value}
                    onChange={field.handleChange}
                  />
                )}
              </form.Field>

              <form.Field name="password">
                {(field) => (
                  <TextInput
                    name="password"
                    type="password"
                    label={t(`${PAGE}.password_label`)}
                    autoComplete="password"
                    minLength={12}
                    maxLength={128}
                    required
                    placeholder={t(`${PAGE}.password_placeholder`)}
                    value={field.state.value}
                    onChange={field.handleChange}
                  />
                )}
              </form.Field>

              <form.Field name="confirmPassword">
                {(field) => (
                  <TextInput
                    name="confirmPassword"
                    type="password"
                    label={t(`${PAGE}.confirm_password_label`)}
                    minLength={12}
                    maxLength={128}
                    required
                    placeholder={t(`${PAGE}.password_placeholder`)}
                    value={field.state.value}
                    onChange={field.handleChange}
                  />
                )}
              </form.Field>

              <div>
                <AuthButton loading={loading}>{t(`${PAGE}.submit`)}</AuthButton>
              </div>
            </form>
          </div>
        </div>
        <ReturnToLoginLink
          navigationHandler={navigationHandler}
          className="max-w pt-10 text-sm text-spark-text-secondary"
        />
      </div>
    </AuthLayout>
  );
}
